import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { setRequestOpstina } from "../api/http";
import { http } from "../api/http";

export interface AdminUnit {
  id: number; code: string; name: string; kind?: string; orchards?: number; producers?: number; area_ha?: number;
}

interface Ctx {
  opstina: string | null;
  setOpstina: (c: string | null) => void;
  units: AdminUnit[];
  unitName: (code?: string | null) => string;
}

const OpstinaCtx = createContext<Ctx>({ opstina: null, setOpstina: () => {}, units: [], unitName: (c) => c ?? "" });
const KEY = "agrisense.opstina";

/** Opština izabrana u zaglavlju: prazno = sve opštine u opsegu korisnika. Promjena osvježava sve upite. */
export function OpstinaProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [opstina, setState] = useState<string | null>(() => {
    try { return localStorage.getItem(KEY) || null; } catch { return null; }
  });
  setRequestOpstina(opstina);
  const unitsQ = useQuery({
    queryKey: ["admin-units-header"], staleTime: 5 * 60_000,
    queryFn: () => http.get<AdminUnit[]>("/api/v1/admin-units"),
  });
  const units = unitsQ.data ?? [];
  useEffect(() => {
    // izabrana opština više nije u opsegu korisnika -> poništi
    if (opstina && unitsQ.data && !unitsQ.data.some((u) => u.code === opstina)) setOpstina(null);
  }, [unitsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setOpstina = (c: string | null) => {
    setRequestOpstina(c);
    setState(c);
    try { if (c) localStorage.setItem(KEY, c); else localStorage.removeItem(KEY); } catch { /* bez trajnog cuvanja */ }
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "admin-units-header" && q.queryKey[0] !== "me" });
  };
  const value = useMemo(() => ({
    opstina, setOpstina, units,
    unitName: (c?: string | null) => units.find((u) => u.code === c)?.name ?? c ?? "",
  }), [opstina, units]); // eslint-disable-line react-hooks/exhaustive-deps
  return <OpstinaCtx.Provider value={value}>{children}</OpstinaCtx.Provider>;
}

export const useOpstina = () => useContext(OpstinaCtx);
